// A tiny external store holding the FIFO prompt queue. Only the head shows;
// the rest wait (ADR 0010). Read it with useSyncExternalStore.
import { createElement } from 'react'
import { MdOutlineDelete } from 'react-icons/md'
import { validate, type PromptRequest } from './queue'

export interface PromptEntry {
  /** Unique per call, so a late click on a gone prompt can't answer the next. */
  key: number
  request: PromptRequest
}

interface Pending extends PromptEntry {
  settle: (actionId: string | null) => void
}

let queue: readonly Pending[] = []
let sequence = 0
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The prompt on screen, or null. Stable between changes. */
export function getHead(): PromptEntry | null {
  return queue[0] ?? null
}

/**
 * Ask the user. Resolves with the chosen action's id; Escape and the scrim
 * give the cancel action's id. Rejects when the request is invalid.
 */
export function confirm(request: PromptRequest): Promise<string | null> {
  try {
    validate(request)
  } catch (error) {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)))
  }
  return new Promise((resolve) => {
    sequence += 1
    queue = [...queue, { key: sequence, request, settle: resolve }]
    emit()
  })
}

/**
 * Answer the prompt on screen. Ignored unless `key` is the head and
 * `actionId` is one of its actions.
 */
export function resolveHead(key: number, actionId: string): void {
  const head = queue[0]
  if (!head || head.key !== key) return
  if (!head.request.actions.some((a) => a.id === actionId)) return
  queue = queue.slice(1)
  emit()
  head.settle(actionId)
}

/**
 * Drop every prompt (sign-out, route change). Pending promises settle with
 * **null**, never with a cancel action nobody chose.
 */
export function clearPrompts(): void {
  const pending = queue
  if (pending.length === 0) return
  queue = []
  emit()
  for (const entry of pending) entry.settle(null)
}

export interface ConfirmDeleteOptions {
  title?: string
  message?: string
}

/** react-admin's delete confirmation, as the portal shows it. True only for Confirm. */
export async function confirmDelete(options: ConfirmDeleteOptions = {}): Promise<boolean> {
  const answer = await confirm({
    title: options.title ?? 'Delete',
    message: options.message ?? 'Are you sure you want to delete this item?',
    actions: [
      { id: 'cancel', label: 'Cancel', cancel: true },
      { id: 'confirm', label: 'Confirm', tone: 'danger', icon: createElement(MdOutlineDelete) }
    ]
  })
  return answer === 'confirm'
}
